import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './ui/Modal';
import CompactSelectPicker from './ui/CompactSelectPicker';
import { Lock, Link, Copy, Check, Info, ShieldCheck } from 'lucide-react';
import { generateSecretLinkPayload } from '../utils/secretLinkCrypto';
import { createSecretLink, revokeSecretLink } from '../services/secretLinkService';
import { copyTextToClipboard } from '../utils/clipboard';

/**
 * One-time secret link modal for the Secrets Vault.
 *
 * `item` is a decrypted vault item ({ ...envelope, data }). The item is
 * encrypted client-side (utils/secretLinkCrypto.js) before anything leaves
 * the app; the server stores only ciphertext. The AES key rides exclusively
 * in the URL `#k=` fragment, which is only ever displayed/copied here —
 * never sent to any endpoint.
 */
const VaultSecretLinkModal = ({ isOpen, onClose, item }) => {
  const { t } = useTranslation();

  const [expiresInHours, setExpiresInHours] = useState(24);
  const [maxViews, setMaxViews] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // { url (with #k= fragment), token, expiresAt, maxViews }
  const [linkData, setLinkData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [revoked, setRevoked] = useState(false);

  const EXPIRY_OPTIONS = [
    { value: 1, label: t('secretLink.expiry1h', '1 hour') },
    { value: 24, label: t('secretLink.expiry24h', '24 hours') },
    { value: 168, label: t('secretLink.expiry7d', '7 days') },
  ];

  const VIEWS_OPTIONS = [
    { value: 1, label: t('secretLink.views1', '1 view (burn after reading)') },
    { value: 3, label: t('secretLink.views3', '3 views') },
    { value: 10, label: t('secretLink.views10', '10 views') },
  ];

  useEffect(() => {
    if (!isOpen) return;
    setExpiresInHours(24);
    setMaxViews(1);
    setLoading(false);
    setError(null);
    setLinkData(null);
    setCopied(false);
    setRevoking(false);
    setRevoked(false);
  }, [isOpen]);

  const handleGenerate = async () => {
    if (!item || loading) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await generateSecretLinkPayload(item);
      const resp = await createSecretLink({
        ciphertext: payload.ciphertext,
        iv: payload.iv,
        alg: payload.alg,
        // Vault item names allow up to 200 chars but the backend caps the
        // link label at 120 — truncate so a long name doesn't 422.
        label: item.name ? item.name.slice(0, 120) : null,
        itemType: item.type || null,
        expiresInHours,
        maxViews,
      });
      setLinkData({
        // Key fragment lives only in this URL — displayed and copied, never sent.
        url: `${resp.url}#k=${payload.keyFragment}`,
        token: resp.token,
        expiresAt: resp.expires_at,
        maxViews: resp.max_views,
      });
    } catch (err) {
      setError(err.message || t('secretLink.createFailed', 'Failed to create secret link'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!linkData?.url || revoked) return;
    const ok = await copyTextToClipboard(linkData.url);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      setError(t('secretLink.copyFailed', 'Failed to copy to clipboard'));
    }
  };

  const handleRevoke = async () => {
    if (!linkData?.token || revoking || revoked) return;
    setRevoking(true);
    setError(null);
    try {
      await revokeSecretLink(linkData.token);
      setRevoked(true);
    } catch (err) {
      setError(err.message || t('secretLink.revokeFailed', 'Failed to revoke secret link'));
    } finally {
      setRevoking(false);
    }
  };

  const handleNewLink = () => {
    setLinkData(null);
    setRevoked(false);
    setCopied(false);
    setError(null);
  };

  const viewsSummary =
    linkData?.maxViews === 1
      ? t('secretLink.burnAfterReading', 'Burns after 1 view')
      : t('secretLink.viewsMax', { defaultValue: '{{count}} views max', count: linkData?.maxViews });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('secretLink.title', 'One-time secret link')}
      subtitle={t('secretLink.subtitle', {
        defaultValue: 'Share "{{name}}" via a self-destructing encrypted link',
        name: item?.name || '',
      })}
      customIcon={<Lock size={22} className="text-blue-500" />}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {!linkData ? (
          <>
            <div>
              <p className="modal-logo-label" style={{ marginBottom: '0.375rem' }}>
                {t('secretLink.expiryLabel', 'Link expiry')}
              </p>
              <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
                <CompactSelectPicker
                  value={expiresInHours}
                  onChange={setExpiresInHours}
                  options={EXPIRY_OPTIONS}
                  aria-label={t('secretLink.expiryLabel', 'Link expiry')}
                />
              </div>
            </div>

            <div>
              <p className="modal-logo-label" style={{ marginBottom: '0.375rem' }}>
                {t('secretLink.viewsLabel', 'Max views')}
              </p>
              <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
                <CompactSelectPicker
                  value={maxViews}
                  onChange={setMaxViews}
                  options={VIEWS_OPTIONS}
                  aria-label={t('secretLink.viewsLabel', 'Max views')}
                />
              </div>
            </div>

            <p className="modal-footer-info" style={{ alignItems: 'flex-start' }}>
              <ShieldCheck size={13} style={{ color: '#16a34a' }} />
              {t(
                'secretLink.e2eExplainer',
                'End-to-end encrypted: the secret is sealed on this device and the key travels only inside the link — the server never sees your secret.'
              )}
            </p>

            {error && (
              <p style={{ margin: 0, fontSize: '0.8125rem', color: '#dc2626' }}>{error}</p>
            )}

            <button
              type="button"
              onClick={handleGenerate}
              disabled={loading || !item}
              className="bg-blue-600"
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#fff',
                border: 'none',
                borderRadius: '0.625rem',
                cursor: loading || !item ? 'not-allowed' : 'pointer',
                opacity: loading || !item ? 0.5 : 1,
                marginTop: '0.25rem',
              }}
            >
              {loading ? (
                <>
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      animation: 'spin 0.8s linear infinite',
                    }}
                  />
                  <span>{t('secretLink.generating', 'Generating…')}</span>
                </>
              ) : (
                <>
                  <Link size={16} />
                  <span>{t('secretLink.generate', 'Generate Secret Link')}</span>
                </>
              )}
            </button>
          </>
        ) : (
          <>
            <div
              style={{
                borderRadius: '0.625rem',
                border: `1px solid ${revoked ? '#e5e7eb' : '#bbf7d0'}`,
                background: revoked ? '#f9fafb' : '#f0fdf4',
                padding: '0.75rem',
                opacity: revoked ? 0.7 : 1,
              }}
            >
              <p
                style={{
                  marginBottom: '0.375rem',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: revoked ? '#6b7280' : '#15803d',
                }}
              >
                {revoked
                  ? t('secretLink.revoked', 'Link revoked — it can no longer be opened.')
                  : t('secretLink.ready', 'Secret link ready')}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="text"
                  readOnly
                  disabled={revoked}
                  value={linkData.url}
                  onFocus={(e) => e.target.select()}
                  className="modal-input modal-input-no-focus-outline modal-input-flat"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '0.375rem 0.625rem',
                    fontSize: '0.75rem',
                    textDecoration: revoked ? 'line-through' : 'none',
                  }}
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  disabled={revoked}
                  className="bg-blue-600"
                  style={{
                    display: 'flex',
                    flexShrink: 0,
                    alignItems: 'center',
                    gap: '0.3rem',
                    padding: '0.375rem 0.625rem',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: revoked ? 'not-allowed' : 'pointer',
                    opacity: revoked ? 0.5 : 1,
                  }}
                >
                  {copied ? <Check size={13} /> : <Copy size={13} />}
                  {copied
                    ? t('secretLink.copied', 'Copied!')
                    : t('secretLink.copy', 'Copy')}
                </button>
              </div>
              {!revoked && (
                <div
                  style={{
                    marginTop: '0.5rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '0.75rem',
                    fontSize: '0.75rem',
                    color: '#16a34a',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                    <Lock size={11} /> {viewsSummary}
                  </span>
                  {linkData.expiresAt && (
                    <span>
                      {t('secretLink.expires', {
                        defaultValue: 'Expires {{date}}',
                        date: new Date(linkData.expiresAt).toLocaleString(),
                      })}
                    </span>
                  )}
                </div>
              )}
            </div>

            {!revoked && (
              <p className="modal-footer-info" style={{ alignItems: 'flex-start' }}>
                <Info size={12} />
                {t(
                  'secretLink.fragmentNote',
                  'The key after "#k=" stays out of server logs, but anyone with the full link can open the secret — share it over a trusted channel.'
                )}
              </p>
            )}

            {error && (
              <p style={{ margin: 0, fontSize: '0.8125rem', color: '#dc2626' }}>{error}</p>
            )}

            <div className="modal-footer-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                type="button"
                onClick={handleRevoke}
                disabled={revoking || revoked}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontSize: '0.75rem',
                  fontFamily: 'inherit',
                  color: revoked ? '#9ca3af' : '#dc2626',
                  cursor: revoking || revoked ? 'not-allowed' : 'pointer',
                  opacity: revoking ? 0.6 : 1,
                  textDecoration: revoked ? 'none' : 'underline',
                }}
              >
                {revoked
                  ? t('secretLink.revokedShort', 'Revoked')
                  : revoking
                    ? t('secretLink.revoking', 'Revoking…')
                    : t('secretLink.revoke', 'Revoke link')}
              </button>
              <button
                type="button"
                onClick={handleNewLink}
                className="modern-button-secondary"
                style={{ padding: '0.375rem 0.75rem', fontSize: '0.8125rem', borderRadius: '0.5rem' }}
              >
                {t('secretLink.newLink', 'Generate new link')}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default VaultSecretLinkModal;
